// @ts-strict-ignore
import { Box3 } from 'three';
import { LabelingTextureData } from '@project/components/label/labeling-texture-data';
import { MetaElementUsageIdentityVO, MetaElementUsageVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { InsertArticleHelper } from '../insert-article.helper';
import { Line } from '@3d-scene/model/line';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageUtils } from '../../util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUtils } from '../../util/article-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '../insert-article-helper.factory';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';

export interface DetachedTextureAreaUsageGroup {
  start: TextureAreaUsageVO;
  end: TextureAreaUsageVO;
}

export interface StoredTextureAreaUsageData {
  identity: MetaElementUsageIdentityVO;
  labelingData: LabelingTextureData;
  connection?: Connection;
}

@Injectable({ providedIn: 'root' })
export class DirectPrintingUtils {
  constructor(
    private _labelingUtils: LabelingUtils,
    private _insertArticleHelperFactory: InsertArticleHelperFactory,
    private _componentUtils: ContainerService,
    private _articleContainerUtils: ArticleContainerUtils,
    private articleContainerService: ArticleContainerService
  ) {}

  public groupDetachedTextureAreaUsages(textureAreaUsages: TextureAreaUsageVO[]): DetachedTextureAreaUsageGroup[] {
    const groups: DetachedTextureAreaUsageGroup[] = [];
    while (textureAreaUsages.length) {
      const currentTextureUsage: TextureAreaUsageVO = textureAreaUsages.shift();
      const newGroup: DetachedTextureAreaUsageGroup = { start: currentTextureUsage, end: currentTextureUsage };
      this.fillGroupRecursively(newGroup, currentTextureUsage, textureAreaUsages, true);
      this.fillGroupRecursively(newGroup, currentTextureUsage, textureAreaUsages, false);
      groups.push(newGroup);
    }
    return groups;
  }

  private fillGroupRecursively(
    group: DetachedTextureAreaUsageGroup,
    textureAreaUsage: TextureAreaUsageVO,
    textureAreaUsages: TextureAreaUsageVO[],
    start: boolean
  ): void {
    if ((start && !textureAreaUsage.previousMergedUsageId) || (!start && !textureAreaUsage.nextMergedUsageId)) {
      return;
    }
    const nextMergedUsageIdGetter = (usage: TextureAreaUsageVO) => (start ? usage.previousMergedUsageId : usage.nextMergedUsageId);
    const existInDeleteArray: TextureAreaUsageVO = textureAreaUsages.find((u) => u.identity.equals(nextMergedUsageIdGetter(textureAreaUsage)));
    if (existInDeleteArray) {
      if (start) {
        group.start = existInDeleteArray;
      } else {
        group.end = existInDeleteArray;
      }
      textureAreaUsages.splice(textureAreaUsages.indexOf(existInDeleteArray), 1);
      if (nextMergedUsageIdGetter(existInDeleteArray)) {
        this.fillGroupRecursively(group, existInDeleteArray, textureAreaUsages, start);
      }
    }
  }

  public findPrevArticleContainer(article: ArticleContainer, excludedGuids: string[] = []): ArticleContainer {
    return this.findNearestArticleContainer(article, excludedGuids, true);
  }

  public findNextArticleContainer(article: ArticleContainer, excludedGuids: string[] = []): ArticleContainer {
    return this.findNearestArticleContainer(article, excludedGuids, false);
  }

  private findNearestArticleContainer(article: ArticleContainer, excludedGuids: string[] = [], isPrevSearch?: boolean): ArticleContainer {
    const baseContainer: ArticleContainer = this.articleContainerService.getBaseRangeOnLineContainer(article);
    if (!baseContainer) {
      return null;
    }
    const insertHelper: InsertArticleHelper = this._insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(baseContainer);
    const articlesOnRail: ArticleContainer[] = insertHelper === null ? [] : insertHelper.getAllConnectedArticles(true);

    let commonLine: Line = insertHelper.calculateCommonArticlesLine([article]);
    if (!commonLine) {
      const articlesOnRail: ArticleContainer[] = this._articleContainerUtils.getAllBackwardArticleContainerOnRailRecursively(article);
      if (ArrayUtils.isNotEmpty(articlesOnRail)) {
        commonLine = insertHelper.calculateCommonArticlesLine([articlesOnRail[0]]);
        ArrayUtils.mergeArrays(
          excludedGuids,
          articlesOnRail.map((a) => a.articleGUID)
        );
      }
    }
    if (!commonLine) {
      return null;
    }

    let nearestArticle: ArticleContainer = null;
    let nearestLine: Line = null;
    for (const articleContainer of articlesOnRail) {
      if ([article.articleGUID, ...excludedGuids].indexOf(articleContainer.articleGUID) >= 0) {
        continue;
      }
      const articleLine: Line = insertHelper.getArticleLine(articleContainer);
      // exclude articles that has further (isPrevSearch === true) or nearer (isPrevSearch === false) position from start position
      if (
        !articleLine ||
        (isPrevSearch && insertHelper.getDistanceFromStart(commonLine.start) < insertHelper.getDistanceFromStart(articleLine.start)) ||
        (!isPrevSearch && insertHelper.getDistanceFromStart(commonLine.end) > insertHelper.getDistanceFromStart(articleLine.end))
      ) {
        continue;
      }
      // check if article is nearer
      if (
        !nearestLine ||
        (isPrevSearch && articleLine.end.distanceTo(commonLine.start) < nearestLine.end.distanceTo(commonLine.start)) ||
        (!isPrevSearch && articleLine.start.distanceTo(commonLine.end) < nearestLine.start.distanceTo(commonLine.end))
      ) {
        nearestLine = articleLine;
        nearestArticle = articleContainer;
      }
    }
    return nearestArticle;
  }

  public checkConnectionRectanglesAlignment(rectangle1: Rectangle3D, rectangle2: Rectangle3D): boolean {
    return (
      rectangle1 &&
      rectangle2 &&
      MathUtils.isRectanglesNormalsEqual(rectangle1, rectangle2) &&
      MathUtils.isRectanglesVerticallyAligned(rectangle1, rectangle2)
    );
  }

  public isConnectionRectanglesIntersected(rectangle1: Rectangle3D, rectangle2: Rectangle3D): boolean {
    const bbox1: Box3 = new Box3().setFromPoints(rectangle1.toArray());
    const bbox2: Box3 = new Box3().setFromPoints(rectangle2.toArray());
    return bbox1.intersectsBox(bbox2);
  }

  public getNearestRectangleFromArray(rectanglesArray: Rectangle3D[], rectangle: Rectangle3D): Rectangle3D {
    let min: number = Infinity;
    let nearest: Rectangle3D;
    rectanglesArray.forEach((r) => {
      const distance: number = MathUtils.calculateDistanceBetweenRectangles(r, rectangle);
      if (distance < min) {
        nearest = r;
        min = distance;
      }
    });
    return nearest;
  }

  public getTextureConnectionsRecursively(articleContainer: ArticleContainer, excludeLabelings: boolean = true): Connection[] {
    if (!articleContainer || (excludeLabelings && ArticleUtils.isLabelingArticle(articleContainer.article))) {
      return [];
    }
    const textureAreas: Connection[] = articleContainer.connections.filter(connection => connection.metaElementUsage instanceof TextureAreaUsageVO);
    const forwardConnected: ArticleContainer[] = this._componentUtils
      .findArticleContainersInGlobalByGuids(ArticleUsageUtils.getAcceptingAreaIds(articleContainer.articleUsage))
      .filter((ac) => !!ac);
    if (forwardConnected) {
      forwardConnected.forEach((c) => ArrayUtils.mergeArrays(textureAreas, this.getTextureConnectionsRecursively(c)));
    }
    return textureAreas;
  }

  public splitAndRestoreTextureConnections(connections: Connection[]): void {
    connections.forEach((connection) => {
      const textureUsage: MetaElementUsageVO = connection.metaElementUsage;
      if (!(textureUsage instanceof TextureAreaUsageVO)) {
        return;
      }
      if (connection.articleContainer) {
        connection.articleContainer.restoreConnection(connection.connectionName, true);
      }
      let prevMergeAvailable: boolean = true;
      let nextMergeAvailable: boolean = true;
      if (!textureUsage.previousMergedUsageId && textureUsage.nextMergedUsageId) {
        // if connection is start connection in merged list
        const nextConnection: Connection = this._labelingUtils.findNextMergedConnection(connection);
        if (nextConnection) {
          const nextTextureUsage: TextureAreaUsageVO = nextConnection.metaElementUsage as TextureAreaUsageVO;
          nextTextureUsage.previousMergedUsageId = null;
          nextMergeAvailable = false;
        }
      } else {
        // if connection is middle or last connection in merged list
        const prevConnection: Connection = this._labelingUtils.findPreviousMergedConnection(connection);
        if (prevConnection) {
          const prevTextureUsage: TextureAreaUsageVO = prevConnection.metaElementUsage as TextureAreaUsageVO;
          prevTextureUsage.nextMergedUsageId = null;
          prevMergeAvailable = false;
        }
        const nextConnection: Connection = this._labelingUtils.findNextMergedConnection(connection);
        if (nextConnection) {
          const nextTextureUsage: TextureAreaUsageVO = nextConnection.metaElementUsage as TextureAreaUsageVO;
          nextTextureUsage.previousMergedUsageId = null;
          nextMergeAvailable = false;
        }
      }
      if (!prevMergeAvailable) {
        textureUsage.previousMergedUsageId = null;
      }
      if (!nextMergeAvailable) {
        textureUsage.nextMergedUsageId = null;
      }
    });
  }
}
